import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { API_URL } from '../api';
import Cookies from 'js-cookie';
import { hasRole } from './userPosition';

const GlobalDataContext = createContext();

export const useGlobalData = () => useContext(GlobalDataContext);

export const GlobalDataProvider = ({ children }) => {
    const [userProfile, setUserProfile] = useState(null);
    const [hospitalsList, setHospitalsList] = useState([]);
    const [rawHospitals, setRawHospitals] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchUserProfile = async () => {
        try {
            const response = await api.get(`${API_URL}/user/profile/`);
            setUserProfile(response.data);

            // Sync local storage if needed, as some parts of app rely on it
            if (response.data) {
                localStorage.setItem('position', response.data.position);
                localStorage.setItem('hospital', response.data.hospital);
                localStorage.setItem('hospital_id', response.data.hospital_id);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
        }
    };

    const fetchHospitals = async () => {
        try {
            const response = await api.get(`${API_URL}/hospitals/`);
            if (response.status === 200) {
                const data = response.data;
                let hospitalsData = [];
                if (data && data.results && Array.isArray(data.results)) {
                    hospitalsData = data.results;
                } else if (Array.isArray(data)) {
                    hospitalsData = data;
                }
                setRawHospitals(hospitalsData);

                // Process data for sidebar
                const hospitalItems = [{
                    'icon': 'A', 'name': 'All Hospitals', 'path': '/hospitals/'
                }];

                hospitalsData.forEach((hospital) => {
                    if (hospital && hospital.name && hospital.id) {
                        const icon = hospital.name.charAt(0).toUpperCase();
                        const name = hospital.name.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ');
                        const hospitalId = hospital.id;
                        const path = `/hospitals/${encodeURIComponent(name)}/${hospitalId}/`;

                        hospitalItems.push({
                            'icon': icon,
                            'name': name,
                            'path': path,
                            "hospitalId": hospitalId
                        });
                    }
                });
                setHospitalsList(hospitalItems);
            }
        } catch (error) {
            console.error('Error fetching hospitals:', error);
            // Fallback
            setHospitalsList([{
                'icon': 'A', 'name': 'All Hospitals', 'path': '/hospitals/'
            }]);
        }
    };

    const fetchUnreadNotifications = async () => {
        // Only fetch for Admin/Super User
        if (hasRole(['Admin', 'Super User'])) {
            try {
                const response = await api.get(`${API_URL}/notifications/?page_size=1000`);
                const unread = response.data.results.filter(n => !n.is_read).length;
                setUnreadCount(unread);
            } catch (error) {
                console.error("Failed to fetch unread count", error);
            }
        }
    };

    const [availableYears, setAvailableYears] = useState([]);

    const fetchAvailableYears = async () => {
        try {
            const response = await api.get(`${API_URL}/measures/years/`);
            if (response.status === 200 && response.data.years) {
                const years = Array.isArray(response.data.years) ? response.data.years : [];
                const sortedYears = years.sort((a, b) => b - a);
                setAvailableYears(sortedYears);
            }
        } catch (error) {
            console.error('Error fetching available years:', error);
            // Fallback to current year range if fails
            const currentYear = new Date().getFullYear();
            setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
        }
    };

    const refreshData = async () => {
        const token = Cookies.get('accessToken');
        if (token) {
            await Promise.all([
                fetchUserProfile(),
                fetchHospitals(),
                fetchUnreadNotifications(),
                fetchAvailableYears()
            ]);
        }
        setLoading(false);
    };

    useEffect(() => {
        refreshData();
    }, []);

    return (
        <GlobalDataContext.Provider value={{
            userProfile,
            hospitalsList,
            rawHospitals,
            unreadCount,
            availableYears,
            loading,
            refreshData,
            setUnreadCount
        }}>
            {children}
        </GlobalDataContext.Provider>
    );
};
